import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import type { GoogleSignInServiceResponse } from '@freelancer/google-sign-in';
import { GoogleSignInService } from '@freelancer/google-sign-in';
import { Pwa } from '@freelancer/pwa';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import type { Observable, Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-google-button',
  template: `
    @if (isBrowser) {
      <!-- special div that google's SDK loads the search button into -->
      <div
        id="google-id"
        class="GoogleButtonHolder"
        [class.hide]="isNative && !(googleSsoStatus$ | flAsync)"
      ></div>
      @if (!(googleSsoStatus$ | flAsync)) {
        <app-login-signup-method-button
          [provider]="'google'"
          [disabled]="!isBrowser || !isGoogleLoaded"
          (click)="handleGoogleClick()"
        >
        </app-login-signup-method-button>
      }
    } @else {
      <app-login-signup-method-button
        [provider]="'google'"
        [disabled]="!isBrowser"
        (click)="handleGoogleClick()"
      >
      </app-login-signup-method-button>
    }
  `,
  styleUrls: ['./google-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  isNative: boolean;
  isBrowser = false;
  isGoogleLoaded = false;
  isMobileViewport = false;
  googleSsoStatus$: Observable<boolean>;

  private credentialResponseSubscription: Subscription;

  @Input() currentMode: 'login' | 'signup' = 'login';
  @Input() buttonWidth = 336;
  @Input() buttonWidthTablet = 352;

  @Output() signInComplete = new EventEmitter();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private googleSignInService: GoogleSignInService,
    private pwa: Pwa,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.isMobileViewport =
        // eslint-disable-next-line no-restricted-globals, no-restricted-properties
        window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
    }

    // eslint-disable-next-line no-restricted-syntax
    this.isNative = this.pwa.isNative();
    this.googleSsoStatus$ = this.googleSignInService.getLoadStatusObservable();
  }

  async ngAfterViewInit(): Promise<void> {
    if (this.isBrowser) {
      await this.googleSignInService.loadGoogleSSO(
        'google-id',
        Math.min(
          this.isMobileViewport ? this.buttonWidth : this.buttonWidthTablet,
          // eslint-disable-next-line no-restricted-globals, no-restricted-properties
          window.innerWidth,
        ),
      );

      if (!this.isNative) {
        this.credentialResponseSubscription =
          this.googleSignInService.credential$.subscribe(
            (response: GoogleSignInServiceResponse) => {
              this.handleCredential(response.credential);
            },
          );
      }

      this.isGoogleLoaded = true;
      this.cd.markForCheck();
    }
  }

  async handleGoogleClick(): Promise<void> {
    this.signInComplete.emit();
  }

  /** Handle credentials from web SSO logic  */
  private handleCredential(credential: string): void {
    if (this.isBrowser) {
      this.googleSignInService.setCredential(credential);
      this.handleGoogleClick();
    }
  }

  ngOnDestroy(): void {
    if (!this.isNative) {
      this.googleSignInService.cancelGoogleSignIn();
    }
    if (this.credentialResponseSubscription) {
      this.credentialResponseSubscription.unsubscribe();
    }
    this.googleSignInService.closeGoogleOneTapPrompt();
  }
}
